import { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NotificationsButton from '../NotificationsButton';
import UserSettingsPopover from '../UserSettingsPopover';
import {
  IconLayoutDashboard,
  IconMessage,
  IconCalendarEvent,
  IconBriefcase,
  IconClipboardList,
  IconFileInvoice,
  IconSettings,
  IconUser,
  IconUsers,
  IconPlus,
  IconHelpCircle,
  IconMenu2,
} from '@tabler/icons-react';
import { useSidebar } from '../Sidebar/SidebarContext';

const routeConfig: Record<string, { titleKey: string; icon: React.ReactNode; tooltipKey: string }> =
  {
    '/dashboard': {
      titleKey: 'dashboard',
      icon: <IconLayoutDashboard size={16} stroke={1.75} />,
      tooltipKey: 'dashboardTooltip',
    },
    '/inbox': {
      titleKey: 'inbox',
      icon: <IconMessage size={16} stroke={1.75} />,
      tooltipKey: 'inboxTooltip',
    },
    '/appointments': {
      titleKey: 'appointments',
      icon: <IconCalendarEvent size={16} stroke={1.75} />,
      tooltipKey: 'appointmentsManagementTooltip',
    },
    '/jobs': {
      titleKey: 'jobs',
      icon: <IconBriefcase size={16} stroke={1.75} />,
      tooltipKey: 'jobsManagementTooltip',
    },
    '/estimates': {
      titleKey: 'estimates',
      icon: <IconClipboardList size={16} stroke={1.75} />,
      tooltipKey: 'estimatesManagementTooltip',
    },
    '/invoices': {
      titleKey: 'invoices',
      icon: <IconFileInvoice size={16} stroke={1.75} />,
      tooltipKey: 'invoicesManagementTooltip',
    },
    '/settings': {
      titleKey: 'settings',
      icon: <IconSettings size={16} stroke={1.75} />,
      tooltipKey: 'settingsTooltip',
    },
    '/profile': {
      titleKey: 'profile',
      icon: <IconUser size={16} stroke={1.75} />,
      tooltipKey: 'profileTooltip',
    },
    '/service-providers': {
      titleKey: 'serviceProviders',
      icon: <IconUsers size={16} stroke={1.75} />,
      tooltipKey: 'serviceProvidersManagementTooltip',
    },
    '/request-service': {
      titleKey: 'requestService',
      icon: <IconPlus size={16} stroke={1.75} />,
      tooltipKey: 'requestServiceTooltip',
    },
  };

const getRouteConfig = (pathname: string) => {
  if (routeConfig[pathname]) return routeConfig[pathname];
  for (const [route, config] of Object.entries(routeConfig)) {
    if (pathname.startsWith(route + '/')) return config;
  }
  return routeConfig['/dashboard'];
};

const defaultTooltips: Record<string, string> = {
  dashboardTooltip: 'Overview of your account activity, upcoming jobs, and financial summary.',
  inboxTooltip: 'View and manage your messages with service providers.',
  appointmentsManagementTooltip: 'Schedule and track all your service appointments.',
  jobsManagementTooltip: 'View and manage all your service jobs from request to completion.',
  estimatesManagementTooltip: 'Review and approve cost estimates from your service providers.',
  invoicesManagementTooltip: 'View, track, and pay invoices from your service providers.',
  settingsTooltip: 'Manage your account preferences and notification settings.',
  profileTooltip: 'View and update your personal profile information.',
  serviceProvidersManagementTooltip: 'Browse and manage your connected service providers.',
  requestServiceTooltip: 'Submit a new service request to a provider.',
};

const ContentHeader = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const config = getRouteConfig(location.pathname);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const helpRef = useRef<HTMLButtonElement>(null);
  const { openMobile } = useSidebar();

  const tooltipText =
    t(config.tooltipKey) !== config.tooltipKey
      ? t(config.tooltipKey)
      : (defaultTooltips[config.tooltipKey] ?? '');

  return (
    <>
      <div className="flex h-[60px] flex-shrink-0 items-center justify-between gap-2 border-b border-ds-border bg-white px-4 relative md-768:gap-0 md-768:px-8">
        {/* Page Title with icon */}
        <div className="flex min-w-0 items-center gap-2 text-ds-text-primary">
          <button
            type="button"
            onClick={openMobile}
            // Framed, the same white rounded card the detail-page back control
            // uses. Bare, it read as loose glyph floating beside the page icon.
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-[rgba(39,39,74,0.0784)] bg-white text-ds-text-secondary shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-colors hover:bg-ds-bg-secondary md-768:hidden"
            aria-label={t('openMenu')}
          >
            <IconMenu2 size={20} stroke={1.75} />
          </button>
          <span className="hidden md-768:inline-flex">{config.icon}</span>
          <h1 className="truncate text-[18px] font-semibold leading-[135%] font-inter-display md-768:text-[20px]">
            {t(config.titleKey)}
          </h1>
        </div>

        {/* Right: Actions */}
        <div className="flex flex-shrink-0 items-center gap-2 md-768:gap-3">
          <NotificationsButton />

          {/* Help tooltip */}
          <div className="relative hidden md-768:block">
            <button
              ref={helpRef}
              onMouseEnter={() => setTooltipVisible(true)}
              onMouseLeave={() => setTooltipVisible(false)}
              className="flex items-center justify-center text-ds-text-tertiary hover:text-ds-text-secondary transition-colors"
              aria-label="Page help"
            >
              <IconHelpCircle size={18} stroke={1.5} />
            </button>
            {tooltipVisible && tooltipText && (
              <div className="absolute z-50 right-0 top-8 w-64 bg-gray-900 text-white text-xs rounded-ds py-2 px-3 leading-relaxed shadow-ds-md pointer-events-none">
                {tooltipText}
                <div className="absolute -top-1 right-2 w-2 h-2 bg-gray-900 rotate-45" />
              </div>
            )}
          </div>

          <UserSettingsPopover />
        </div>
      </div>
    </>
  );
};

export default ContentHeader;
